import React, { useEffect, useMemo, useState } from 'react';
import Slider from 'react-slick';

const Reviews = () => {
    const [reviews, setReviews] = useState([]);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const sliderSettings = useMemo(
        () => ({
            infinite: true,
            slidesToShow: 1,
            slidesToScroll: 1,
            arrows: false,
            dots: false,
            autoplay: true,
            autoplaySpeed: 3000,
            pauseOnHover: true,
            pauseOnFocus: true,
            adaptiveHeight: true,
            speed: 500,
        }),
        []
    );

    // Fetch reviews from the backend
    useEffect(() => {
        const fetchReviews = async () => {
            try {
                setIsLoading(true);
                const response = await fetch('/api/reviews');
                if (!response.ok) {
                    throw new Error('Failed to fetch reviews');
                }
                const data = await response.json();

                if (Array.isArray(data)) {
                    const nextNotice = data.length ? null : 'No reviews available yet.';
                    setNotice(nextNotice);
                    setReviews(data);
                } else if (data && typeof data === 'object' && typeof data.message === 'string') {
                    setNotice(data.message);
                    setReviews([]);
                } else {
                    setNotice('No reviews available yet.');
                    setReviews([]);
                }
            } catch (err) {
                setError(err.message);
            } finally {
                setIsLoading(false);
            }
        };

        fetchReviews();
    }, []);

    // Error handling
    if (error) {
        return (
            <div className="alert alert-danger text-center" role="alert">
                {error}
            </div>
        );
    }

    // Loading state (ONLY while request in-flight)
    if (isLoading) {
        return (
            <div className="text-center my-5">
                <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    // Empty state (request completed but no reviews)
    if (!reviews.length) {
        return (
            <div className="container my-5 text-center">
                <h2 className="mb-3">Top Customer Reviews</h2>
                <p className="mb-0">{notice || 'No reviews available yet.'}</p>
            </div>
        );
    }

    // Helper to render star ratings
    const renderStars = (rating) => {
        const stars = [];
        for (let i = 0; i < 5; i++) {
            stars.push(
                <i
                    key={i}
                    className={`bi bi-star${i < rating ? '-fill' : ''}`}
                    style={{ color: '#ffc107', marginRight: '2px' }}
                ></i>
            );
        }
        return stars;
    };

    return (
        <div className="container my-5 reviews-section">
            <h2 className="text-center mb-4">Top Customer Reviews</h2>
            <div className="reviews-slider-wrap">
                <Slider {...sliderSettings}>
                    {reviews.map((review, index) => (
                        <div key={index} className="reviews-slide">
                            <div className="card shadow-sm reviews-floating-card">
                                <div className="card-body">
                                    <div className="d-flex align-items-start justify-content-between gap-3 mb-2">
                                        <div>
                                            <h5 className="mb-1">{review.author_name}</h5>
                                            <div className="text-warning">{renderStars(review.rating)}</div>
                                        </div>
                                    </div>
                                    <p className="card-text mb-0">{review.text}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </Slider>
            </div>
        </div>
    );
};

export default Reviews;
